import {
	useEffect,
	useMemo,
	useSyncExternalStore,
	type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

import { accountSession } from "../account-session/browserAccountSession.ts";
import {
	UserContext,
	type UserContextType,
} from "./user-context.ts";

const VISIBILITY_REFRESH_INTERVAL_MS = 30_000;

export const UserProvider = ({ children }: { children: ReactNode }) => {
	const location = useLocation();
	const snapshot = useSyncExternalStore(
		accountSession.subscribe,
		accountSession.getSnapshot,
		accountSession.getSnapshot,
	);
	const isDiscordCallback = location.pathname === "/auth/callback/discord";

	useEffect(() => {
		if (
			isDiscordCallback
			|| accountSession.getSnapshot().profileStatus !== "idle"
		) {
			return;
		}
		void accountSession.initialize();
	}, [isDiscordCallback]);

	useEffect(() => {
		let lastRefreshAttemptAt = 0;
		const refreshWhenVisible = () => {
			if (document.hidden) return;
			const current = accountSession.getSnapshot();
			if (
				!current.account
				&& current.profileStatus !== "transient-error"
			) {
				return;
			}
			const now = Date.now();
			if (now - lastRefreshAttemptAt < VISIBILITY_REFRESH_INTERVAL_MS) return;
			lastRefreshAttemptAt = now;
			void accountSession.refresh();
		};
		document.addEventListener("visibilitychange", refreshWhenVisible);
		return () =>
			document.removeEventListener("visibilitychange", refreshWhenVisible);
	}, []);

	const contextValue = useMemo<UserContextType>(
		() => ({
			user: snapshot.account,
			loading: snapshot.loading,
			logout: accountSession.logout,
			fetchProfile: accountSession.refresh,
			beginDiscordSignIn: accountSession.beginDiscordSignIn,
			completeDiscordSignIn: accountSession.completeDiscordSignIn,
			cancelDiscordSignIn: accountSession.cancelDiscordSignIn,
		}),
		[snapshot.account, snapshot.loading],
	);

	return (
		<UserContext.Provider value={contextValue}>
			{children}
		</UserContext.Provider>
	);
};
